import { isScreenshotMode } from "@constants/common";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { getParentMetadata } from "@helpers/content/metadata";
import { randomizeDistribution } from "@helpers/mocked-helpers";
import { useAppStore } from "@hooks/useAppStore";
import { useAppTemplate } from "@hooks/useAppTemplate";
import useMixedItemDist from "@hooks/useMixedItemDist";
import { normalizeDate } from "@memorang/helpers/date";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
	hasLikelyPracticeRecommendation,
	useHydratedStudyRecommendations,
} from "../../recommendations/hooks/useHydratedStudyRecommendations";
import { useDonutProgress } from "../use-donut-progress";
import { useHomeSummaryTip } from "../useHomeSummaryTip";
import { useParsedRecommendationContext } from "../useParsedRecommendationContext";
import { useRecommendedTestsForSkill } from "../useRecommendedTestsForSkill";
import { useUserLeaderboard } from "../useUserLeaderboard";
import {
	getLanguageLearningStats,
	getLanguageLearningStatsPlaceholder,
} from "./helpers";
import { useHomeHeaderIcon } from "./useHomeHeaderIcon";

export const useHomeHeader = () => {
	const tip = useHomeSummaryTip();
	const { t } = useTranslation();
	const {
		currentExamContext: { examName, examDate },
	} = useExamContext();

	const { data: commonDistribution, isLoading } = useMixedItemDist();

	const examEnabled = useAppStore((state) => state.exam.enabled);
	const allowChangeExamDate = useAppStore(
		(state) => state.exam.allowChangeExamDate,
	);

	const { isLanguageLearningApp } = useAppTemplate();

	const showXpEarned = !(isLanguageLearningApp || examEnabled);
	const { data: userLeaderboardData } = useUserLeaderboard({
		timePeriod: "all_time",
		enabled: showXpEarned,
	});

	const { distribution } = getParentMetadata(
		commonDistribution?.contents || [],
	);

	const { donutProgressData, donutProgressColorScale } =
		useDonutProgress(distribution);

	const parsedExamDate = normalizeDate(examDate);

	const { exams: startableTests, examsLoading } = useRecommendedTestsForSkill();
	const hydrated = useHydratedStudyRecommendations({
		sessionCount: 3,
		durationMinutes: 10,
	});
	const recommendationContext = useParsedRecommendationContext();

	// Memoized: the chip predicate runs a per-skill engine selection.
	const stats = useMemo(() => {
		if (!isLanguageLearningApp) {
			return undefined;
		}
		if (isLoading) {
			return getLanguageLearningStatsPlaceholder();
		}
		return getLanguageLearningStats({
			tags: commonDistribution?.tags ?? [],
			recommendationContext,
			startableTests,
			hasPracticeForSkill: (skillId) =>
				hasLikelyPracticeRecommendation(hydrated, [skillId]),
			t,
		});
	}, [
		isLanguageLearningApp,
		isLoading,
		commonDistribution,
		recommendationContext,
		startableTests,
		hydrated,
		t,
	]);

	const headerIcon = useHomeHeaderIcon();

	return {
		examIcon: headerIcon,
		examName,
		examDate: parsedExamDate,
		examEnabled: Boolean(examEnabled),
		accuracy: commonDistribution?.accuracy
			? Math.round(commonDistribution.accuracy)
			: 0,
		distribution: isScreenshotMode
			? randomizeDistribution(distribution)
			: distribution,
		donutProgressData,
		donutProgressColorScale,
		xpEarned: userLeaderboardData?.xp,
		allowChangeExamDate: allowChangeExamDate !== false,
		isLoading,
		statsLoading:
			isLanguageLearningApp && (examsLoading || hydrated.status === "LOADING"),
		stats,
		tip,
	};
};
